import { useQuery } from '@tanstack/react-query';
import { Layer, RadioButtonGroup } from 'grommet';
import { ChevronLeftIcon } from 'lucide-preact';
import { useCallback, useEffect, useState } from 'preact/hooks';

import { PrimaryButton } from '@/components/Buttons';
import CloseButton from '@/components/modals/CloseButton';
import { getUserRetentionOffersQueryOptions } from '@/queries/products';
import { ParsedUserProduct } from '@/queries/products/types';

import CancellationFlowOffer from './CancellationFlowOffer';

type OfferType = 'percentageOff' | 'pauseSubscription' | 'oneMonthOnUs';

interface ReasonOption {
  text: string;
  nextStep?: number;
  offer?: OfferType;
}

interface ReasonStep {
  step: number;
  question: string;
  options: ReasonOption[];
}

const originalReasons: ReasonStep[] = [
  {
    step: 1,
    question: "Thinking of cancelling? Happy to help. What's the reason if we might ask?",
    options: [
      { text: "Can't afford it right now", nextStep: 2 },
      { text: 'Not really using it right now', nextStep: 3 },
      { text: 'Having a technical issue', nextStep: 4 },
    ],
  },
  {
    step: 2,
    question: 'Could you tell us more about not being able to afford it right now?',
    options: [
      { text: 'Trying to cut costs', offer: 'pauseSubscription' },
      { text: 'I would like more animals / content / hosted events', offer: 'percentageOff' },
      { text: 'I would like improved features and functionality', offer: 'percentageOff' },
    ],
  },
  {
    step: 3,
    question: "Could you tell us the biggest reason you're not using zoolife?",
    options: [
      { text: 'Maybe if there was more content to explore', offer: 'percentageOff' },
      { text: 'My school / teaching / research project is now complete', offer: 'pauseSubscription' },
      { text: 'Maybe if it was more convenient to use', offer: 'percentageOff' },
      { text: 'Nothing would make me use zoolife more' },
      { text: "Maybe if I had more time or wasn't so busy", offer: 'percentageOff' },
    ],
  },
  {
    step: 4,
    question: 'Could you tell us more about what we can fix for you?',
    options: [
      { text: 'Not enough content to explore', offer: 'percentageOff' },
      { text: 'Loading issues with the video feeds', offer: 'oneMonthOnUs' },
      { text: 'Other', offer: 'oneMonthOnUs' },
    ],
  },
];

interface CancelReasonsModalProps {
  userProduct: ParsedUserProduct;
  productLookupKey: string | undefined;
  onClose: () => void;
  onConfirmCancel: (cancellationReason: string) => void;
}

const CancelReasonsModal = ({ userProduct, productLookupKey, onClose, onConfirmCancel }: CancelReasonsModalProps) => {
  const [reasons, setReasons] = useState<ReasonStep[]>(() => originalReasons);
  const [currentStep, setCurrentStep] = useState(1);
  const [reason, setReason] = useState<ReasonOption | Record<string, never>>({});
  const [text, setText] = useState('');
  const [showOffer, setShowOffer] = useState(false);

  const { data: offers } = useQuery({
    ...getUserRetentionOffersQueryOptions(userProduct._id),
    select: (data) => data.eligibleOffers,
  });

  useEffect(() => {
    if (offers) {
      const updatedReasons = originalReasons.map((step) => ({
        ...step,
        options: step.options.map((option) => {
          if (option.offer) {
            if (offers.includes(option.offer)) {
              return option;
            }
            return { ...option, offer: undefined };
          }
          return option;
        }),
      }));
      setReasons(updatedReasons);
    }
  }, [offers]);

  const onChangeHandler = useCallback(
    (event: { target: { value: string } }) => {
      const selected = reasons
        .find(({ step }) => step === currentStep)
        ?.options.find(({ text: t }) => t === event.target.value);
      if (selected) setReason(selected);

      setTimeout(() => {
        document.getElementById('cancelText')?.focus();
        document.getElementById('cancelButton')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    },
    [currentStep, reasons],
  );

  // What the user chose and typed, as `"<reason>: <note>"` — sent with the cancel or the offer.
  const cancellationReason = `${reason && 'text' in reason ? reason.text : 'User cancelled'}: ${text}`;

  const currentReason = reasons.find(({ step }) => step === currentStep);

  const handleNext = () => {
    if (reason && 'offer' in reason && reason.offer) {
      setShowOffer(true);
    } else if (reason && 'nextStep' in reason && reason.nextStep) {
      setCurrentStep(reason.nextStep);
    } else {
      onConfirmCancel(cancellationReason);
    }
  };

  const handleBack = () => {
    const previousStep = reasons.find(({ options }) =>
      options.some((opt) => 'nextStep' in opt && opt.nextStep === currentStep),
    );
    if (previousStep) {
      setCurrentStep(previousStep.step);
    }
  };

  const shouldShowTextArea =
    currentStep > 1 &&
    reason &&
    'text' in reason &&
    reason.text &&
    currentReason?.options.some((option) => option.text === reason.text);

  const handleNoThanks = () => {
    onConfirmCancel(cancellationReason);
  };

  return (
    <Layer
      onClickOutside={onClose}
      onEsc={onClose}
      className="flex w-[90%] flex-col rounded-xl bg-white text-[var(--charcoal)] mobile:w-[90%] desktop:w-[620px]"
    >
      <CloseButton varient="grey" onClick={onClose} className="absolute right-2 top-2" />
      {showOffer && reason && 'offer' in reason && reason.offer ? (
        <CancellationFlowOffer
          userProduct={userProduct}
          offerType={reason.offer}
          reason={cancellationReason}
          productLookupKey={productLookupKey}
          onNoThanks={handleNoThanks}
          onAfterClaimOfferClose={onClose}
        />
      ) : (
        <>
          <h4 className="m-6 mb-1 mt-8 text-xl font-bold leading-8">{currentReason?.question}</h4>
          <div className="relative overflow-y-auto px-10 pb-10 pt-5 mobile:px-5">
            <div>
              <RadioButtonGroup
                name="reasons"
                className="flex flex-col gap-1.5 [&_[class*='StyledRadioButtonBox']]:mr-2.5 [&_[class*='StyledRadioButtonBox']]:border [&_[class*='StyledRadioButtonBox']]:border-[var(--charcoal)] hover:[&_[class*='StyledRadioButtonBox']]:border-black [&_[class*='StyledRadioButtonContainer']]:flex [&_[class*='StyledRadioButtonContainer']]:items-center hover:[&_[class*='StyledRadioButtonContainer']]:text-black [&_[tabindex='0']+div_svg]:fill-[var(--primaryGreen)]"
                options={currentReason?.options.map(({ text: t }) => t) ?? []}
                value={reason && 'text' in reason ? reason.text : undefined}
                onChange={(event: any) => onChangeHandler(event)}
              />

              {shouldShowTextArea && (
                <textarea
                  id="cancelText"
                  cols={30}
                  rows={10}
                  maxLength={1500}
                  placeholder="Tell us more…"
                  value={text}
                  onChange={(e) => setText((e.target as HTMLTextAreaElement).value)}
                  className="mt-5 w-full resize-none rounded-xl border border-black bg-white px-3 py-2.5 text-base font-light leading-[16.5px] shadow-[0_4px_4px_0_rgba(0,0,0,0.25),0_4px_15px_0_rgba(0,0,0,0.1)_inset]"
                />
              )}

              <div id="cancelButton" className="flex items-center justify-between pt-5">
                <button
                  type="button"
                  className="preflight preflight-button flex cursor-pointer items-center p-0 text-sm font-semibold leading-8 text-stone-500"
                  onClick={handleBack}
                  tabIndex={0}
                >
                  {currentStep > 1 && (
                    <>
                      <ChevronLeftIcon className="size-6" />
                      Back
                    </>
                  )}
                </button>

                <PrimaryButton
                  type="button"
                  label="Submit"
                  onClick={handleNext}
                  disabled={
                    !!(
                      !reason ||
                      !('text' in reason) ||
                      !reason.text ||
                      (currentStep > 1 && !text && !('nextStep' in reason ? reason.nextStep : undefined))
                    )
                  }
                />
              </div>
            </div>
          </div>
        </>
      )}
    </Layer>
  );
};

export default CancelReasonsModal;
